import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { useIsMobile, useIsTablet } from '../../hooks/useMediaQuery';
interface ModalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: string;
}
export function ModalDialog({
  isOpen,
  onClose,
  title,
  children,
  footer,
  maxWidth = 'max-w-lg'
}: ModalDialogProps) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  useEffect(() => {
    if (isOpen) {
      // Lock body scroll and prevent any background interaction
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      return () => {
        document.body.style.overflow = 'unset';
        document.body.style.touchAction = 'auto';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);
  return createPortal(<AnimatePresence data-id="element-240">
      {isOpen && <>
          {/* Full-screen backdrop — rendered via portal at body level for guaranteed coverage */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease: 'easeOut'
      }} className="fixed inset-0 bg-text-primary/40 backdrop-blur-sm" style={{
        zIndex: 9990,
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh'
      }} onClick={onClose} data-id="element-241" />
          <div className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none" style={{
        zIndex: 9991,
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh'
      }} data-id="element-242">
            <motion.div initial={isMobile ? {
          opacity: 0,
          y: '100%'
        } : {
          opacity: 0,
          scale: 0.96
        }} animate={isMobile ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 1,
          scale: 1
        }} exit={isMobile ? {
          opacity: 0,
          y: '100%'
        } : {
          opacity: 0,
          scale: 0.96
        }} transition={isMobile ? {
          type: 'spring',
          damping: 25,
          stiffness: 300
        } : {
          duration: 0.18,
          ease: 'easeOut'
        }} className={`
                w-full bg-white pointer-events-auto flex flex-col
                ${isMobile ? 'h-full rounded-none' : `${maxWidth} rounded-medical-lg shadow-modal max-h-[calc(100vh-64px)]`}
              `} data-id="element-243">
              {/* Header */}
              <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-clinical-gray-100 shrink-0" data-id="element-244">
                <h3 className="text-section-title text-text-primary" data-id="element-245">
                  {title}
                </h3>
                <button onClick={onClose} className="p-2 md:p-1 text-text-tertiary hover:text-text-primary hover:bg-clinical-gray rounded-full transition-colors" aria-label="Chiudi" data-id="element-246">
                  <XIcon size={24} className="md:w-5 md:h-5" data-id="element-247" />
                </button>
              </div>

              {/* Scrollable content body */}
              <div className="px-4 md:px-6 pt-4 md:pt-6 pb-4 md:pb-5 lg:pb-6 overflow-y-auto flex-1" data-id="element-248">
                {children}
              </div>

              {/* Footer — separated by spacing, not divider */}
              {footer && <div className="px-4 md:px-6 pb-4 md:pb-5 lg:pb-6 pt-2 mt-0 shrink-0 safe-area-bottom" data-id="element-249">
                  <div className="mt-0 flex flex-col sm:flex-row justify-end gap-3" data-id="element-250">
                    {footer}
                  </div>
                </div>}
            </motion.div>
          </div>
        </>}
    </AnimatePresence>, document.body);
}